import { useTranslations } from '@vocab/react';
import {
  Box,
  Heading,
  IconInfo,
  Inline,
  Stack,
  Text,
  TooltipRenderer,
} from 'braid-design-system';
import { useEffect } from 'react';

import AnimatedHeight from 'src/components/AnimatedHeight/AnimatedHeight';
import { useAnalyticsFacade } from 'src/modules/AnalyticsFacade';
import { useIsHomepageGenAIChatEnabled } from 'src/store/featureFlags/hooks';
import { useDispatch } from 'src/store/react';
import { saveSolReferencesForRecsAction } from 'src/store/results';

import { CareerFeedAIAssistant } from '../CareerFeedAIAssistant/CareerFeedAIAssistant';
import { CareerFeedAIAssistantEmptyState } from '../CareerFeedAIAssistant/CareerFeedAIAssistantEmptyState/CareerFeedAIAssistantEmptyState';
import { useCareerFeedAIAssistant } from '../CareerFeedAIAssistant/hooks/useCareerFeedAIAssistant';
import { CardsStack } from '../SharedComponent/CardsStack/CardsStack';
import { ErrorState } from '../SharedComponent/ErrorState/ErrorState';
import { useSignedInDashboardContext } from '../SignedInDashboardContextProvider';
import { isCareerFeedJob } from '../hooks/feed-types';

import translations from './.vocab';
import { CareerFeedEmptyState } from './CareerFeedEmptyState';
import { CareerFeedItems } from './CareerFeedItems';
import { CareerFeedLoadingState } from './CareerFeedLoadingState';

// This is only for the loading state.
// When we change the count for web to 100, we need to change this too.
const CAREER_FEED_LIMIT = 50;

const CareerFeedContent = () => {
  const {
    careerFeedResult: { loading, feedItems, isRefreshLoading },
  } = useSignedInDashboardContext();
  const isHomepageGenAIChatEnabled = useIsHomepageGenAIChatEnabled();
  const { persistedCandidateChatContext } = useCareerFeedAIAssistant();

  const isRenderingGenAIRecs = Boolean(
    isHomepageGenAIChatEnabled && persistedCandidateChatContext,
  );

  const isRecsLoading = loading || isRefreshLoading;

  if (!isRecsLoading && !feedItems) {
    return <ErrorState section="careerFeed" />;
  }

  if (!isRecsLoading && feedItems?.length === 0) {
    if (isRenderingGenAIRecs) {
      return <CareerFeedAIAssistantEmptyState />;
    }

    return <CareerFeedEmptyState />;
  }

  return (
    <CardsStack>
      {isHomepageGenAIChatEnabled && <CareerFeedAIAssistant />}

      {isRecsLoading ? (
        <CareerFeedLoadingState
          length={CAREER_FEED_LIMIT}
          keyPrefix="feedRecs-onTop"
        />
      ) : (
        <CareerFeedItems feedItems={feedItems} />
      )}
    </CardsStack>
  );
};

export const CareerFeed = () => {
  const dispatch = useDispatch();
  const { t } = useTranslations(translations);
  const analyticsFacade = useAnalyticsFacade();
  const {
    careerFeedResult: {
      loading,
      feedItems,
      tracking: { solMetadata, serviceToken },
    },
  } = useSignedInDashboardContext();

  const { messages: aiAssistantMessage, progress: aiAssistantProgress } =
    useCareerFeedAIAssistant();

  useEffect(() => {
    if (solMetadata) {
      analyticsFacade.recommendedJobsImpression({ solMetadata });
    }

    if (serviceToken) {
      analyticsFacade.careerFeedDisplayed({ serviceToken });
    }

    const recommendations =
      feedItems
        ?.filter((feedItem) => isCareerFeedJob(feedItem))
        .map((feedItem) => ({
          job: feedItem,
          solMetadata: feedItem?.solMetadata,
        })) ?? [];
    if (recommendations.length) {
      dispatch(saveSolReferencesForRecsAction(recommendations, 'feedJobs'));
    }
  }, [analyticsFacade, dispatch, feedItems, serviceToken, solMetadata]);

  return (
    <Stack space="small">
      <Inline space="xxsmall" alignY="center">
        <Heading level="3">{t('Recommended jobs')}</Heading>
        <TooltipRenderer
          id=""
          tooltip={
            <Text align="center" size="small">
              {t('Based on your profile, job views and applications.')}
            </Text>
          }
        >
          {({ triggerProps }) => (
            <Box aria-label="Info" {...triggerProps}>
              <IconInfo size="xsmall" />
            </Box>
          )}
        </TooltipRenderer>
      </Inline>

      <AnimatedHeight
        changeKeys={[
          loading,
          feedItems,
          aiAssistantMessage,
          aiAssistantProgress,
        ]}
      >
        <CareerFeedContent />
      </AnimatedHeight>
    </Stack>
  );
};
